import React, {useEffect, useRef, useState} from 'react';
import { ImageType } from '../../index';
import {
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip
} from '@mui/material';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import HorizontalRuleTwoToneIcon from '@mui/icons-material/HorizontalRuleTwoTone';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import DoDisturbOnTwoToneIcon from '@mui/icons-material/DoDisturbOnTwoTone';
import { deleteImage } from '../../../../api/files';
import MuiAlert, { AlertColor } from '@mui/material/Alert';

const columns = [
  { id: 'url', label: 'Image preview' },
  { id: 'isPaid', label: 'Is paid' },
  { id: 'isPublic', label: 'Is public' },
  { id: 'isSystem', label: 'Is system' },
];

const ListPicturesComponent = (
  {
    list,
    setList,
    activatedTab,
    getMoreImage,
    hasMore,
    isLoading,
    setIsLoading
  }: {
    list: ImageType[],
    setList(value: ImageType[]): void,
    activatedTab: string,
    getMoreImage(): void,
    hasMore: boolean,
    isLoading: boolean,
    setIsLoading(value: boolean): void
  }
) => {
  const [openSnackbar, setOpenSnackbar] = useState<{isOpen: boolean, alertType: AlertColor, message: string}>({
    isOpen: false,
    alertType: 'success',
    message: ''
  });
  const tableEl = useRef<any>(null);
  const [distanceBottom, setDistanceBottom] = useState(0);
  
  const handleRowItem = (valueType: string, value: string | number | boolean) => {
    switch (valueType) {
      case 'url': return <img src={value.toString()} alt={value.toString()} className="w-[250px] rounded-md" />
      case 'isPublic':
      case 'isSystem':
      case 'isPaid': return value ? <CheckCircleTwoToneIcon color="success" /> : <DoDisturbOnTwoToneIcon color="error" />
      default: return <span>{value || <HorizontalRuleTwoToneIcon color="disabled" />}</span>;
    }
  };
  
  const handleDeleteImage = async (imageType: ImageType) => {
    try {
      await deleteImage(imageType.id);
      const updatedImageList = list.filter((item) => item.id !== imageType.id);
      setList(updatedImageList);
      setOpenSnackbar({ isOpen: true, message: 'Image has been successfully deleted!', alertType: 'success'});
    } catch (e) {
      setOpenSnackbar({ isOpen: true, message: 'Image has been successfully deleted!', alertType: 'error'});
    }
  };
  
  const loadMore = () => {
    const loadItems = async () => {
      await new Promise(resolve =>
        setTimeout(() => {
          getMoreImage();
          resolve(true);
        }, 2000)
      )
    };
    
    loadItems();
  };
  
  useEffect(() => {
    const scrollListener = () => {
      let bottom = tableEl?.current?.scrollHeight - tableEl?.current?.clientHeight;
      
      if (!distanceBottom) {
        setDistanceBottom(Math.round(bottom * 0.2));
      }
      
      if ((tableEl.current.scrollTop > bottom - distanceBottom) && hasMore && !isLoading) {
        loadMore();
        setIsLoading(true);
      }
    };
    
    if (tableEl && tableEl.current) {
      tableEl.current.addEventListener('scroll', scrollListener, false);
      
      return () => tableEl?.current?.removeEventListener('scroll', scrollListener, false);
    }
  }, [tableEl.current, list, distanceBottom]);
  
  useEffect(() => {
    if (tableEl.current) tableEl.current.scrollTo(0, 0);
  }, [activatedTab])
  
  return (
    <div className="mt-10">
      <TableContainer sx={{ maxHeight: "60vh", width: '100%', position: 'relative', marginBottom: '20px' }} ref={tableEl}>
        <Table stickyHeader aria-labelledby="tableTitle" size={"small"}>
          <TableHead>
            <TableRow>
              {list.length > 0 && columns.map((item: any) => (
                <TableCell
                  key={item.id}
                  align="left"
                  style={{ minWidth: 50 }}
                >
                  <TableSortLabel
                    className="capitalize font-bold"
                  >
                    {item.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {list.length > 0 && (
                <TableCell key="action" align="center">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row: any, index: number) => {
              return (
                <TableRow hover tabIndex={-1} key={index.toString()}>
                  {columns.map((column) => {
                    return (
                      <TableCell key={column.id} align="left">
                        {handleRowItem(column.id, row[column.id])}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    key="delete"
                    align="center"
                  >
                    <Tooltip title="Delete image" placement="top">
                      <DeleteTwoToneIcon color="error" className="cursor-pointer" onClick={() => handleDeleteImage(row)} />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={openSnackbar.isOpen}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
      >
        <MuiAlert
          onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
          severity={openSnackbar.alertType}
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  )
};

export default ListPicturesComponent;